import React, { useState, useEffect } from 'react';
import {Tooltip, Select, Form, Input, Button, message, Spin, Typography, Checkbox, Space, Tabs, Steps, Alert, Divider, Modal} from 'antd';
import { 
InfoCircleOutlined, DollarOutlined, DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import CostAnalytics from './CostAnalytics';
import SystemRequirements from './SystemRequirements';
import VectorStoreManagement from './VectorStoreManagement';
import WhitelabelingTab from './WhitelabelingTab';
import ApiBalanceDisplay from './ApiBalanceDisplay';
import { GoogleIntegrationLocked, CostManagementLocked, KnowledgeBaseLocked, WhitelabelingLocked } from './lockedTabContent';

// Add these helper functions at the top of your Settings component
const isLiteVersion = () => {
  return process.env.REACT_APP_VERSION === 'lite';
};

const isTabLocked = (tabKey) => {
  if (!isLiteVersion()) return false;
  const lockedTabs = ['google-integration', 'cost-management', 'knowledge-base', 'customization'];
  return lockedTabs.includes(tabKey);
};

const isSectionHidden = (sectionKey) => {
  if (!isLiteVersion()) return false;
  const hiddenSections = ['license-management'];
  return hiddenSections.includes(sectionKey);
};

const { TabPane } = Tabs;
const { Step } = Steps;
const { Text, Paragraph, Title } = Typography;

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState('inactive');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [aiWorkflowApiKey, setAiWorkflowApiKey] = useState('');
  const [selectedModels, setSelectedModels] = useState([]);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [googleRedirectUri, setGoogleRedirectUri] = useState('');
  const [googleSetupStep, setGoogleSetupStep] = useState(0);
  const [isSavingGoogleSettings, setIsSavingGoogleSettings] = useState(false);
  const [isGoogleIntegrated, setIsGoogleIntegrated] = useState(false);
  const [analyticsOptOut, setAnalyticsOptOut] = useState(false);
  const [localApiKeys, setLocalApiKeys] = useState({});
  const [roles, setRoles] = useState({});
  const [taskRoles, setTaskRoles] = useState(['administrator']);
  const [hasApiKeys, setHasApiKeys] = useState({
  openrouter: false
});

  

  const aiApiModels = [
    { name: 'OpenAI', label: "OpenAI", value: 'openai', apiKeyName: 'openai_api_key', 
      helpLink: "https://platform.openai.com/api-keys",
      helpText: "Click here to activate your OpenAI API key. Make sure you add at least $5 credit to your account." },
    { name: 'Perplexity', label: "Perplexity", value: 'perplexity', apiKeyName: 'perplexity_api_key',
      helpLink: "https://www.perplexity.ai/settings/api",
      helpText: "Click here to activate your Perplexity API key" },
    { name: 'OpenRouter', label: "OpenRouter", value: 'openrouter', apiKeyName: 'openrouter_api_key',
      helpLink: "https://openrouter.ai/settings/keys",
      helpText: "Click here to get your OpenRouter API key",
      extraText: "OpenRouter allows access to multiple AI models with a single API key. It provides a unified interface for various models from different providers." },
      { 
        name: 'Fal.ai', 
        label: "Fal.ai (Multimedia Generation)", 
        value: 'fal_ai', 
        apiKeyName: 'fal_api_key',
        helpLink: "https://fal.ai/dashboard/keys",
        helpText: "Click here to get your Fal.ai API key",
        extraText: "Fal.ai provides various models for image and video generation. Prices vary by model." 
      }
  ];

  const otherServiceModels = [
    { name: 'Firecrawl', label: "Firecrawl (web scraping and crawling)", value: 'firecrawl', apiKeyName: 'firecrawl_api_key',
      helpLink: "https://firecrawl.dev/app",
      helpText: "Click here to get your Firecrawl API key" },
    { name: 'LlamaParse', label: "LlamaParse (document parsing)", value: 'llamaparse', apiKeyName: 'llamaparse_api_key',
      helpLink: "https://cloud.llamaindex.ai/api-key",
      helpText: "Click here to get your LlamaParse API key" },
    { name: 'Unsplash', label: "Unsplash (image search)", value: 'unsplash', apiKeyName: 'unsplash_api_key',
      helpLink: "https://unsplash.com/developers",
      helpText: "Click here to get your Unsplash API key" },
  ];


  const renderApiKeySection = (models, title, groupType) => (
    <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
      <Form.Item label={`Enable ${title}`}>
        <Checkbox.Group 
          options={models.map(model => ({
            label: model.label,
            value: model.value,
            disabled: model.disabled
          }))} 
          value={selectedModels.filter(model => 
            models.some(m => m.value === model)
          )} 
          onChange={(checkedValues) => handleModelSelection(checkedValues, groupType)}
        />
      </Form.Item>

      {models.map(model => (
        selectedModels.includes(model.value) && !model.disabled && (
          <Form.Item
            key={model.value}
            name={model.apiKeyName}
            label={
              <span>
                {model.name} API Key
                {/* Updated condition to use hasApiKeys */}
                {(model.value === 'openrouter') && 
                  hasApiKeys[model.value] && (
                    <ApiBalanceDisplay apiType={model.value} />
                )}
              </span>
            }
            rules={[{ required: true, message: `Please input your ${model.name} API key!` }]}
            extra={
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Note: For security reasons, API keys are encrypted and cannot be viewed after saving.
                </Text>
                <a href={model.helpLink} target="_blank" rel="noopener noreferrer">
                  {model.helpText}
                </a>
                {model.extraText && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    {model.extraText}
                    {model.value === 'openrouter' && (
                      <a href="https://openrouter.ai/docs/models" target="_blank" rel="noopener noreferrer">
                        {" "}Click here to see all available models and prices.
                      </a>
                    )}
                  </Text>
                )}
              </>
            }
          >
            <Input.Password />
          </Form.Item>
        )
      ))}
    </div>
  );

  useEffect(() => {
    fetchSettings();
    checkGoogleIntegrationStatus();

    const fetchRoles = async () => {
      try {
          const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/roles`, {
              headers: { 'X-WP-Nonce': config.apiSettings.nonce }
          });
          setRoles(response.data);
      } catch (error) {
          console.error('Error fetching roles:', error);
          message.error('Failed to fetch roles');
      }
  };
  fetchRoles();
  }, []);

  const checkGoogleIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/google-integration-status`, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      setIsGoogleIntegrated(response.data.integrated);
      if (response.data.integrated) {
        setGoogleClientId(response.data.client_id);
        setGoogleClientSecret(response.data.client_secret);
      }
    } catch (error) {
      console.error('Error checking Google integration status:', error);
    }
  };

    const fetchSettings = async () => {
    setFetchingSettings(true);
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/settings`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Set form values for all fields
      form.setFieldsValue({
        openai_api_key: response.data.openai_api_key || '',
        perplexity_api_key: response.data.perplexity_api_key || '',
        openrouter_api_key: response.data.openrouter_api_key || '',
        fal_api_key: response.data.fal_api_key || '',
        firecrawl_api_key: response.data.firecrawl_api_key || '',
        llamaparse_api_key: response.data.llamaparse_api_key || '',
        license_key: response.data.license_key || '',
        unsplash_api_key: response.data.unsplash_api_key || '', 
        google_client_id: response.data.google_client_id || '',
        google_client_secret: response.data.google_client_secret || '',
        task_roles: response.data.task_roles || ['administrator'],
      });
      
      // Check if API keys exist (even if masked)
      setHasApiKeys({
        openrouter: !!response.data.openrouter_api_key
      });
      
      // Set all other state values
      setLicenseStatus(response.data.license_status || 'inactive');
      setLicenseExpiry(response.data.license_expiry || '');
      setAiWorkflowApiKey(response.data.ai_workflow_api_key || '');
      setSelectedModels(response.data.selected_models || []);
      setGoogleClientId(response.data.google_client_id || '');
      setGoogleClientSecret(response.data.google_client_secret || '');
      setGoogleRedirectUri(response.data.google_redirect_uri || '');
      setAnalyticsOptOut(response.data.analytics_opt_out || false);
      
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('Failed to fetch settings');
    } finally {
      setFetchingSettings(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const dataToSend = {
        ...values,
        selected_models: selectedModels,
        google_client_id: googleClientId,
        google_client_secret: googleClientSecret,
        analytics_opt_out: analyticsOptOut,
        task_roles: values.task_roles || ['administrator']
      };
      
      // Remove any circular references or React components
      const cleanData = JSON.parse(JSON.stringify(dataToSend));
  
      const response = await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/settings`, cleanData, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      message.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const generateNewApiKey = async () => {
    try {
      const response = await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/generate-api-key`, {}, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      setAiWorkflowApiKey(response.data.ai_workflow_api_key);
      message.success('New API key generated successfully');
    } catch (error) {
      console.error('Error generating API key:', error);
      message.error('Failed to generate new API key');
    }
  };

  const toggleLicense = async () => {
    const action = licenseStatus === 'active' ? 'deactivate' : 'activate';
    try {
        setLoading(true);

        const response = await axios.post(
            `${config.apiSettings.root}wp-ai-workflows/v1/${action}-license`,
            {
                license_key: form.getFieldValue('license_key')
            },
            {
                headers: {
                    'X-WP-Nonce': config.apiSettings.nonce
                }
            }
        );

        if (response.data.success) {
            setLicenseStatus(action === 'activate' ? 'active' : 'inactive');
            message.success(response.data.message);
            fetchSettings();
        } else {
            handleLicenseError(response.data);
        }
    } catch (error) {
        if (error.response?.data) {
            handleLicenseError(error.response.data);
        } else {
            Modal.error({
                title: 'License Activation Failed',
                content: 'An unexpected error occurred. Please try again later.'
            });
        }
        setLicenseStatus('inactive');
    } finally {
        setLoading(false);
    }
  };

const handleLicenseError = (errorData) => {
    switch (errorData.error_code) {
        case 'domain_mismatch':
            Modal.error({
                title: 'Domain Mismatch',
                content: (
                    <div>
                        <Alert
                            type="error"
                            message="Wrong Domain"
                            description={
                                <div>
                                    <p><strong>Registered Domain:</strong> {errorData.registered_domain}</p>
                                    <p><strong>Current Domain:</strong> {errorData.current_domain}</p>
                                    <p>The license must first be activated on the domain you registered during purchase.
                                       After the first activation, you can use it on other domains.</p>
                                </div>
                            }
                            style={{ marginBottom: 16 }}
                        />
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text type="secondary">Need help with your license?</Text>
                            <Button type="primary">
                                <a 
                                    href="https://wpaiworkflowautomation.com/contact/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                >
                                    Contact Support
                                </a>
                            </Button>
                        </Space>
                    </div>
                ),
                width: 500
            });
            break;

        case 'too_many_attempts':
            Modal.error({
                title: 'Too Many Attempts',
                content: 'Please wait 15 minutes before trying again.'
            });
            break;

        case 'missing_key':
            Modal.error({
                title: 'Missing License Key',
                content: 'Please enter your license key.'
            });
            break;

        default:
            Modal.error({
                title: 'License Activation Failed',
                content: (
                    <div>
                        <p>{errorData.message}</p>
                        <Divider />
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text type="secondary">
                                If this issue persists, please contact our support team.
                            </Text>
                            <Button type="primary">
                                <a 
                                    href="https://wpaiworkflowautomation.com/contact/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                >
                                    Contact Support
                                </a>
                            </Button>
                        </Space>
                    </div>
                )
            });
    }
  };

  const handleModelSelection = (checkedValues, modelGroup) => {
    // Get all the possible values from both groups
    const aiModelValues = aiApiModels.map(model => model.value);
    const otherModelValues = otherServiceModels.map(model => model.value);
    
    // Keep selections from the other group
    let newSelection;
    if (modelGroup === 'ai') {
      // Keep other services selections and add new AI selections
      const currentOtherSelections = selectedModels.filter(model => otherModelValues.includes(model));
      newSelection = [...currentOtherSelections, ...checkedValues];
    } else {
      // Keep AI selections and add new other service selections
      const currentAiSelections = selectedModels.filter(model => aiModelValues.includes(model));
      newSelection = [...currentAiSelections, ...checkedValues];
    }

    setSelectedModels(newSelection);
  };

  const generateGoogleRedirectUri = async () => {
    try {
      const response = await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/generate-google-redirect-uri`, {}, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      setGoogleRedirectUri(response.data.redirect_uri);
      message.success('Google Redirect URI generated successfully');
    } catch (error) {
      console.error('Error generating Google Redirect URI:', error);
      message.error('Failed to generate Google Redirect URI');
    }
  };

  const initiateGoogleAuth = () => {
    const width = 600;
    const height = 600;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    const popup = window.open(
      `${config.apiSettings.root}wp-ai-workflows/v1/get-google-auth-url?_wpnonce=${config.apiSettings.nonce}`,
      'Google OAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  
    // Poll the popup to check when it's closed
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        checkGoogleIntegrationStatus();
      }
    }, 500);
  };

  const saveGoogleSettings = async () => {
    if (!googleClientId || !googleClientSecret) {
      message.error('Please enter both Client ID and Client Secret');
      return;
    }
  
    setIsSavingGoogleSettings(true);
    try {
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/settings`,
        {
          google_client_id: googleClientId,
          google_client_secret: googleClientSecret,
          google_redirect_uri: googleRedirectUri
        },
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      message.success('Google settings saved successfully');
      setGoogleSetupStep(3);
    } catch (error) {
      console.error('Error saving Google settings:', error);
      message.error('Failed to save Google settings');
    } finally {
      setIsSavingGoogleSettings(false);
    }
  };

  const resetGoogleIntegration = async () => {
    try {
      await axios.post(`${config.apiSettings.root}wp-ai-workflows/v1/reset-google-integration`, {}, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      setIsGoogleIntegrated(false);
      setGoogleClientId('');
      setGoogleClientSecret('');
      message.success('Google integration has been reset');
    } catch (error) {
      console.error('Error resetting Google integration:', error);
      message.error('Failed to reset Google integration');
    }
  };

  const renderGoogleSetup = () => {
    if (isGoogleIntegrated) {
      return (
        <div>
          <Paragraph>
            <Text strong>Google Integration Status: </Text>
            <Text type="success">Successfully Integrated</Text>
          </Paragraph>
          <Paragraph>
            <Text strong>Client ID: </Text>
            {googleClientId}
          </Paragraph>
          <Paragraph>
            <Text strong>Client Secret: </Text>
            {googleClientSecret}
          </Paragraph>
          <Button onClick={resetGoogleIntegration} type="primary" danger>
            Reset Google Integration
          </Button>
        </div>
      );
    }
  

    return (
      <div>
        <Steps current={googleSetupStep}>
          <Step title="Create Project" description="Create a new project in Google Cloud Console" />
          <Step title="Enable APIs" description="Enable necessary Google APIs" />
          <Step title="Create Credentials" description="Create OAuth client ID and secret" />
          <Step title="Configure OAuth" description="Set up OAuth consent screen" />
        </Steps>
  
        <div style={{ marginTop: '20px' }}>
          {googleSetupStep === 0 && (
            <div>
              <Paragraph>
                1. Go to the <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a>.
                2. Click on the project dropdown and select "New Project".
                3. Give your project a name and click "Create".
              </Paragraph>
              <Button onClick={() => setGoogleSetupStep(1)}>Next</Button>
            </div>
          )}
  
          {googleSetupStep === 1 && (
            <div>
              <Paragraph>
                1. In your new project, go to the "APIs & Services" dashboard.
                2. Click on "+ ENABLE APIS AND SERVICES".
                3. Search for and enable "Google Drive API" and "Google Sheets API".
              </Paragraph>
              <Button onClick={() => setGoogleSetupStep(2)}>Next</Button>
            </div>
          )}
  
          {googleSetupStep === 2 && (
            <div>
              <Paragraph>
                1. In the "APIs & Services" sidebar, click on "Credentials".
                2. Click "Create Credentials" and select "OAuth client ID".
                3. Choose "Web application" as the application type.
                4. Set the authorized redirect URI to:
              </Paragraph>
              <Input value={googleRedirectUri} readOnly />
              <Button onClick={generateGoogleRedirectUri} style={{ marginTop: '10px' }}>Generate Redirect URI</Button>
              <Paragraph>
                5. Click "Create" and note down your Client ID and Client Secret.
              </Paragraph>
              <Form layout="vertical">
                <Form.Item label="Client ID" required>
                  <Input
                    value={googleClientId}
                    onChange={(e) => setGoogleClientId(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Client Secret" required>
                  <Input.Password
                    value={googleClientSecret}
                    onChange={(e) => setGoogleClientSecret(e.target.value)}
                  />
                </Form.Item>
              </Form>
              <Button onClick={saveGoogleSettings} type="primary" loading={isSavingGoogleSettings}>
                Save Google Settings
              </Button>
            </div>
          )}
  
          {googleSetupStep === 3 && (
            <div>
              <Paragraph>
                1. In the "APIs & Services" sidebar, click on "OAuth consent screen".
                2. Choose "External" as the user type and click "Create".
                3. Fill in the required information for your app.
                4. Add the necessary scopes for Google Drive and Google Sheets.
                5. Add test users if needed.
              </Paragraph>
              <Button onClick={initiateGoogleAuth} type="primary">
                Sign in with Google
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (fetchingSettings) {
    return <Spin size="large" />;
  }

return (
  <Tabs defaultActiveKey="1">
    <TabPane tab="General Settings" key="1">
      <Form form={form} layout="vertical" onFinish={onFinish}>
        
        {/* License Management Section - Hidden in Lite */}
        {!isSectionHidden('license-management') && (
          <>
            <Title level={4}>License Management</Title>
            <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
              <Form.Item
                name="license_key"
                label="License Key"
                rules={[{ required: true, message: 'Please input your license key!' }]}
                extra={
                  <div>
                    <a href="https://wpaiworkflowautomation.com/pricing" target="_blank" rel="noopener noreferrer">Purchase a license</a>
                    {licenseStatus === 'active' && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                          For security, the actual license key is masked. To modify or view the key, deactivate the license first.
                        </Text>
                      </div>
                    )}
                  </div>
                }
              >
                <Input 
                  placeholder="Enter your license key" 
                  disabled={licenseStatus === 'active'}
                />
              </Form.Item>

              <Form.Item>
                <Space direction="horizontal">
                  <Text style={{ color: licenseStatus === 'active' ? 'green' : 'red', fontWeight: 'bold' }}>
                    License Status: {licenseStatus === 'active' ? 'Active' : 'Inactive'}
                  </Text>
                  {licenseStatus === 'active' && licenseExpiry && (
                    <Text>
                      Valid until: {new Date(licenseExpiry).toLocaleDateString()}
                    </Text>
                  )}
                  <Button onClick={toggleLicense} type="primary" style={{ marginTop: 8 }}>
                    {licenseStatus === 'active' ? 'Deactivate' : 'Activate'} License
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </>
        )}

        <Title level={4}>System Requirements</Title>
        <SystemRequirements />

        {/* AI API Keys Section */}
        <Title level={4}>AI API Keys</Title>
        {renderApiKeySection(aiApiModels, 'AI Services', 'ai')}

        {/* Other Services Section */}
        <Title level={4}>Other Services</Title>
        {renderApiKeySection(otherServiceModels, 'Other Integrations', 'other')}

        {/* Plugin API Access Section */}
        <Title level={4}>Plugin API Access</Title>
        <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
          <Form.Item label="AI Workflow Automation API Key">
            <Input.Password
              value={aiWorkflowApiKey}
              readOnly
              visibilityToggle={false}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              This is your unique API key for AI Workflow Automation. It's used for authentication when making API calls to your workflows.
            </Text>
            <Button onClick={generateNewApiKey} style={{ marginTop: 8 }}>Generate New API Key</Button>
          </Form.Item>
        </div>

        {/* Task Roles Section */}
        <Title level={4}>Task Management Access</Title>
        <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
          <Form.Item
            label={
              <Space>
                <span>Roles with Task Access</span>
                <Tooltip title="Select which user roles can view and action workflow tasks">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
            }
            name="task_roles"
          >
            <Select
              mode="multiple"
              placeholder="Select roles that can manage tasks"
              style={{ width: '100%' }}
              options={Object.entries(roles).map(([key, label]) => ({
                label: label,
                value: key
              }))}
            />
          </Form.Item>
          <Alert
            message="Note"
            description="Selected roles will be able to view and action their assigned tasks through a dedicated tasks interface. Administrator role always has access."
            type="info"
            showIcon
          />
        </div>

        {/* Analytics Section */}
        <Title level={4}>Analytics Settings</Title>
        <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
          <Form.Item label="Usage Analytics">
            <Alert
              type="info"
              showIcon
              message="Usage Analytics"
              description={
                <div>
                  <p>We collect anonymous usage data to help improve the plugin. This includes:</p>
                  <ul>
                    <li>Installation status (active/inactive)</li>
                    <li>Plugin version and environment info (PHP/WordPress versions)</li>
                    <li>Number of workflows and executions</li>
                    <li>Success rates of workflow executions</li>
                  </ul>
                  <p>We never collect:</p>
                  <ul>
                    <li>Personal information or API keys</li>
                    <li>Workflow content or execution data</li>
                    <li>User data or sensitive information</li>
                  </ul>
                </div>
              }
              style={{ marginBottom: 16 }}
            />
            <Checkbox
              checked={!analyticsOptOut}
              onChange={(e) => setAnalyticsOptOut(!e.target.checked)}
            >
              Help improve AI Workflow Automation by sharing anonymous usage data
            </Checkbox>
          </Form.Item>

          {analyticsOptOut && (
            <Alert
              type="warning"
              showIcon
              message="Analytics Disabled"
              description="Analytics help us understand how the plugin is used and what features need improvement. This data is completely anonymous and helps us make the plugin better for everyone."
              style={{ marginBottom: 16 }}
            />
          )}
        </div>

        {/* Debugging Section */}
        <Title level={4}>Debugging</Title>
        <div style={{ backgroundColor: '#f9f9f9', padding: 24, borderRadius: 8, marginBottom: 24 }}>
          <Form.Item label="Debug Log">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Debug Log"
                description={
                  <div>
                    <p>Generate a diagnostic log to help us troubleshoot and resolve issues with the plugin. This log contains:</p>
                    <ul>
                      <li>WordPress environment details (versions, configuration)</li>
                      <li>AI Workflow Automation plugin configuration and operational status</li>
                      <li>Workflow execution logs including AI service interactions</li>
                      <li>Recent chat activity logs (if chat feature is used)</li>
                    </ul>
                    <p>The log is designed to protect your privacy and security:</p>
                    <ul>
                      <li>API keys and authentication credentials are redacted</li>
                      <li>Personal identifiable information is not included</li>
                      <li>Other plugin data and settings are not collected</li>
                    </ul>
                    <p>For your peace of mind, we recommend reviewing the generated log before sharing it with our support team to ensure no sensitive information is included.</p>
                  </div>
                }
                style={{ marginBottom: 16 }}
              />
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const downloadUrl = `${config.apiSettings.root}wp-ai-workflows/v1/download-log`;
                  
                  // Create temporary link element
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.setAttribute('download', 'wp-ai-workflows-debug.log');
                  
                  // Add nonce to URL
                  const urlWithNonce = `${downloadUrl}?_wpnonce=${config.apiSettings.nonce}`;
                  
                  // Use fetch to handle the download
                  fetch(urlWithNonce, {
                    headers: {
                      'X-WP-Nonce': config.apiSettings.nonce
                    }
                  })
                  .then(response => {
                    if (!response.ok) {
                      throw new Error('Network response was not ok');
                    }
                    return response.blob();
                  })
                  .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    link.href = url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  })
                  .catch(error => {
                    console.error('Error downloading log file:', error);
                    message.error('Failed to download log file');
                  });
                }}
              >
                Download Debug Log
              </Button>
            </Space>
          </Form.Item>
        </div>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save Settings
          </Button>
        </Form.Item>
      </Form>
    </TabPane>

    {/* Google Integration Tab - Locked in Lite */}
    <TabPane 
      tab={
        <span>
          Google Integration
          {isLiteVersion() && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </span>
      } 
      key="2"
    >
      {isTabLocked('google-integration') ? (
        <GoogleIntegrationLocked />
      ) : (
        renderGoogleSetup()
      )}
    </TabPane>

    {/* Cost Management Tab - Locked in Lite */}
    <TabPane 
      tab={
        <span>
          Cost Management
          {isLiteVersion() && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </span>
      } 
      key="3"
    >
      {isTabLocked('cost-management') ? (
        <CostManagementLocked />
      ) : (
        <CostAnalytics />
      )}
    </TabPane>

    {/* Knowledge Base Management Tab - Locked in Lite */}
    <TabPane 
      tab={
        <span>
          Knowledge Base Management
          {isLiteVersion() && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </span>
      } 
      key="4"
    >
      {isTabLocked('knowledge-base') ? (
        <KnowledgeBaseLocked />
      ) : (
        <VectorStoreManagement />
      )}
    </TabPane>

    {/* Customization/Whitelabeling Tab - Locked in Lite (Agency License) */}
    <TabPane 
      tab={
        <span>
          Customization
          {isLiteVersion() && (
            <span style={{ marginLeft: '8px', color: '#722ed1', fontSize: '11px', fontWeight: '500' }}>
              (AGENCY)
            </span>
          )}
        </span>
      } 
      key="5"
    >
      {isTabLocked('customization') ? (
        <WhitelabelingLocked />
      ) : (
        <WhitelabelingTab />
      )}
    </TabPane>
  </Tabs>
);
};

export default Settings;